import { ReactNode } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Eye,
  Edit,
  Trash2,
  MoreVertical,
  CreditCard,
  DollarSign,
  Download,
  RefreshCw,
} from 'lucide-react';

export interface ActionDropdownProps {
  id: string;

  buttonClassName?: string;

  size?: 'sm' | 'icon' | 'default';

  variant?:
    | 'default'
    | 'destructive'
    | 'outline'
    | 'secondary'
    | 'ghost'
    | 'link';

  showView?: boolean;

  showEdit?: boolean;

  showDelete?: boolean;

  showDeposit?: boolean;

  showWithdraw?: boolean;

  showDownload?: boolean;

  showRefresh?: boolean;

  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onDeposit?: () => void;
  onWithdraw?: () => void;
  onDownload?: () => void;
  onRefresh?: () => void;

  additionalItems?: ReactNode;
}

const ActionDropdown = ({
  id,
  buttonClassName,
  size = 'icon',
  variant = 'ghost',
  showView = true,
  showEdit = true,
  showDelete = true,
  showDeposit = false,
  showWithdraw = false,
  showDownload = false,
  showRefresh = false,
  onView,
  onEdit,
  onDelete,
  onDeposit,
  onWithdraw,
  onDownload,
  onRefresh,
  additionalItems,
}: ActionDropdownProps) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={buttonClassName}
          title={`Actions for ${id}`}
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-auto min-w-[160px] whitespace-nowrap"
      >
        {showEdit && (
          <DropdownMenuItem
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            onClick={onEdit}
          >
            <Edit className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">Edit</span>
          </DropdownMenuItem>
        )}
        {showView && (
          <DropdownMenuItem
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            onClick={onView}
          >
            <Eye className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">View</span>
          </DropdownMenuItem>
        )}
        {showRefresh && (
          <DropdownMenuItem
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            onClick={onRefresh}
          >
            <RefreshCw className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">Refresh</span>
          </DropdownMenuItem>
        )}

        {showDelete && (
          <DropdownMenuItem
            className="flex items-center gap-2 text-sm text-red-600 cursor-pointer whitespace-nowrap"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">Delete</span>
          </DropdownMenuItem>
        )}
        {showDeposit && (
          <DropdownMenuItem
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            onClick={onDeposit}
          >
            <CreditCard className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">Deposit Funds</span>
          </DropdownMenuItem>
        )}
        {showWithdraw && (
          <DropdownMenuItem
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            onClick={onWithdraw}
          >
            <DollarSign className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">Withdrawal Funds</span>
          </DropdownMenuItem>
        )}
        {showDownload && (
          <DropdownMenuItem
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            onClick={onDownload}
          >
            <Download className="h-4 w-4 shrink-0" />
            <span className="whitespace-nowrap">Download</span>
          </DropdownMenuItem>
        )}
        {additionalItems}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

ActionDropdown.displayName = 'ActionDropdown';

export default ActionDropdown;
